// PWA — 电源面板组件（Req 6.1, 6.2, 6.3, 6.5 / 任务 9.3）
//
// 进入即显示实例电源状态（running / stopped / starting…）；stopped 时提供「启动」
// 按钮，触发 POST /power/start 后轮询至 running，再探测 /ready 至 ComfyUI 就绪，
// 期间展示进度与预计等待（覆盖 ~40s 冷加载）。
//
// 复用 shared 的 power 逻辑（mapEc2State / decideStart，任务 2.3/2.19）：
// 服务端已用 mapEc2State 归一状态；面板经 powerView（内部调用 decideStart）
// 决定是否可点「启动」。
//
// 本组件为展示型：电源状态由上层 usePower Hook 提供（见 App.tsx），
// 以便页头 pill 与本面板共享同一数据源，避免重复轮询。

import { useEffect, useState } from "react";
import type { UsePowerResult } from "./usePower.js";
import { derivePowerView, progressText } from "./powerView.js";

export interface PowerPanelProps {
  /** usePower 的返回值（由上层 App 持有并下传）。 */
  power: UsePowerResult;
  /** 是否已认证：未认证时面板提示先登录。 */
  authenticated: boolean;
}

export function PowerPanel({
  power,
  authenticated,
}: PowerPanelProps): JSX.Element {
  const {
    state,
    comfyReady,
    instanceId,
    loading,
    startingFlow,
    elapsedSec,
    error,
    refresh,
    start,
    stop,
    mustChangePasscode,
    clearMustChangePasscode,
    changePasscode,
  } = power;

  const view = derivePowerView(state, comfyReady, startingFlow);
  const progress = progressText(view.phase, elapsedSec);

  // 关机（管理员密码保护）的本地 UI 状态。
  const [showStop, setShowStop] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [stopping, setStopping] = useState(false);

  // 修改关机密码弹窗的本地 UI 状态。
  const [showChange, setShowChange] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [changing, setChanging] = useState(false);
  const [changeMsg, setChangeMsg] = useState<string | null>(null);

  // 后端提示"应修改关机密码"（首发临时密码首次使用后）→ 自动弹出修改弹窗。
  useEffect(() => {
    if (mustChangePasscode) setShowChange(true);
  }, [mustChangePasscode]);

  const submitStop = async (): Promise<void> => {
    if (!passcode || stopping) return;
    setStopping(true);
    const ok = await stop(passcode);
    setStopping(false);
    if (ok) {
      // 成功：收起表单并清空密码（状态已转 stopping）。
      setShowStop(false);
      setPasscode("");
    }
    // 失败：保留表单，power.error 已提示"管理员密码错误"。
  };

  const submitChange = async (): Promise<void> => {
    if (changing) return;
    setChangeMsg(null);
    if (newPw.length < 8) {
      setChangeMsg("新密码至少 8 位");
      return;
    }
    if (newPw !== newPw2) {
      setChangeMsg("两次输入的新密码不一致");
      return;
    }
    setChanging(true);
    const res = await changePasscode(curPw, newPw);
    setChanging(false);
    if (res.ok) {
      setShowChange(false);
      setCurPw("");
      setNewPw("");
      setNewPw2("");
      setChangeMsg(null);
      clearMustChangePasscode();
    } else {
      setChangeMsg(res.message ?? "修改失败");
    }
  };

  const closeChange = (): void => {
    setShowChange(false);
    setCurPw("");
    setNewPw("");
    setNewPw2("");
    setChangeMsg(null);
    // 用户"稍后再说"：清掉强制提示，避免反复弹出（下次仍可手动修改）。
    clearMustChangePasscode();
  };

  if (!authenticated) {
    return (
      <section className="card" aria-label="电源面板">
        <h2 className="card__title">电源面板</h2>
        <p className="app__hint">登录后可查看并控制 GPU 实例电源状态。</p>
      </section>
    );
  }

  return (
    <section className="card" aria-label="电源面板">
      <h2 className="card__title">电源面板</h2>

      <div className="power-panel__row">
        <span
          className={`power-pill power-pill--${state}`}
          aria-label={`电源状态：${view.label}`}
        >
          {loading ? "查询中…" : view.label}
        </span>
        {view.phase === "running-ready" && (
          <span className="power-panel__ready" aria-live="polite">
            ComfyUI 已就绪
          </span>
        )}
      </div>

      {/* 进度 / 预计等待（启动中或冷加载中）。 */}
      {progress && (
        <div className="power-panel__progress" role="status" aria-live="polite">
          <div
            className="skeleton skeleton--line"
            aria-hidden="true"
            style={{ marginBottom: 8 }}
          />
          <p className="app__hint" style={{ textAlign: "left", margin: 0 }}>
            {progress}
          </p>
        </div>
      )}

      {error && (
        <p className="app__hint" role="alert">
          {error}
        </p>
      )}

      <div className="power-panel__actions">
        {view.canStart && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={start}
            disabled={startingFlow}
          >
            启动实例
          </button>
        )}
        {view.busy && (
          <button type="button" className="btn" disabled aria-busy="true">
            启动中…
          </button>
        )}
        {!view.canStart && !view.busy && (
          <button
            type="button"
            className="btn"
            onClick={refresh}
            disabled={loading}
          >
            刷新状态
          </button>
        )}
        {/* 关机（管理员专属）：实例已开机时可见，点击后需输入管理员密码。 */}
        {view.canStop && !showStop && (
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => setShowStop(true)}
          >
            关机
          </button>
        )}
        {/* 修改关机密码（管理员）：始终可用。 */}
        {!showChange && (
          <button
            type="button"
            className="btn btn--small"
            onClick={() => setShowChange(true)}
          >
            修改关机密码
          </button>
        )}
      </div>

      {/* 关机确认：需输入管理员密码（受后端 SHA-256 校验保护）。 */}
      {view.canStop && showStop && (
        <div className="power-panel__stop" role="group" aria-label="关机确认">
          <p className="app__hint" style={{ textAlign: "left", margin: "8px 0 6px" }}>
            关机将停止 GPU 实例（正在出图的任务会中断）。请输入管理员密码确认：
          </p>
          <input
            type="password"
            className="submit-form__input"
            placeholder="管理员密码"
            value={passcode}
            autoComplete="off"
            onChange={(e) => setPasscode(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitStop();
            }}
          />
          <div className="power-panel__actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => void submitStop()}
              disabled={!passcode || stopping}
            >
              {stopping ? "关机中…" : "确认关机"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setShowStop(false);
                setPasscode("");
              }}
              disabled={stopping}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* 修改关机密码弹窗（首次使用临时密码后自动弹出，或手动打开）。 */}
      {showChange && (
        <div className="power-panel__stop" role="group" aria-label="修改关机密码">
          <p
            className="app__hint"
            style={{ textAlign: "left", margin: "0 0 6px" }}
          >
            {mustChangePasscode
              ? "首次使用临时关机密码，请设置你自己的新密码（至少 8 位）："
              : "修改管理员关机密码（需输入当前密码，新密码至少 8 位）："}
          </p>
          <input
            type="password"
            className="submit-form__input"
            placeholder="当前密码"
            value={curPw}
            autoComplete="off"
            onChange={(e) => setCurPw(e.currentTarget.value)}
            style={{ marginBottom: 6 }}
          />
          <input
            type="password"
            className="submit-form__input"
            placeholder="新密码（≥8 位）"
            value={newPw}
            autoComplete="new-password"
            onChange={(e) => setNewPw(e.currentTarget.value)}
            style={{ marginBottom: 6 }}
          />
          <input
            type="password"
            className="submit-form__input"
            placeholder="再次输入新密码"
            value={newPw2}
            autoComplete="new-password"
            onChange={(e) => setNewPw2(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitChange();
            }}
          />
          {changeMsg && (
            <p
              className="app__hint"
              role="alert"
              style={{ textAlign: "left", marginTop: 6 }}
            >
              {changeMsg}
            </p>
          )}
          <div className="power-panel__actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submitChange()}
              disabled={!curPw || !newPw || !newPw2 || changing}
            >
              {changing ? "保存中…" : "保存新密码"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={closeChange}
              disabled={changing}
            >
              {mustChangePasscode ? "稍后再说" : "取消"}
            </button>
          </div>
        </div>
      )}

      {instanceId && (
        <p className="app__hint" style={{ textAlign: "left", marginTop: 8 }}>
          实例：{instanceId}
        </p>
      )}
    </section>
  );
}
